import { Employee } from "@finway-group/shared/lib/models"
import UserCompanyProfileInterface from "@finway-group/shared/lib/models/user/userCompanyProfileInterface"
import { useQueryClient } from "@tanstack/react-query"
import { Button, Form, Modal } from "antd"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { ConfirmDeleteOrDemoteEmployeeModal } from "Components/modals/ConfirmDeleteOrDemoteEmployeeModal"
import { ConfirmRemoveSubstituteModal } from "Components/modals/ConfirmRemoveSubstituteModal"
import { useCompanies } from "Shared/hooks/company.hooks"
import { QueryKeyEnum } from "Shared/reactQuery.types"
import { NotificationService, UserService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { getAvailableCompanies } from "Shared/utils/company.utils"

import { invalidateEmployees, useEmployees } from "Shared/queries/employee.queries"
import { fetchAllWorkflows } from "Shared/store/actions/workflow/workflowActions"
import { UserCompanyProfileForm } from "../forms/employeeForm/userCompanyProfile.form"

type UserCompanyProfileModalProps = {
    employee: Employee
    onSubmit: () => void
    isShowing: boolean
    onCancel: () => void
} & (
    | {
          isNewCompanyProfile: false
          companyProfile: UserCompanyProfileInterface & { company: { _id: string; name: string } }
      }
    | {
          isNewCompanyProfile: true
          companyProfile: undefined
      }
)
export const UserCompanyProfileModal = ({ isNewCompanyProfile, isShowing, onCancel, employee, companyProfile, onSubmit }: UserCompanyProfileModalProps) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const [workflowsReplacementApproverId, setWorkflowsReplacementApproverId] = useState<string | undefined>(undefined)
    const [removeSubstituteConfirmed, setRemoveSubstituteConfirmed] = useState<boolean>(false)

    const [showConfirmDeleteOrDemoteEmployeeModal, setShowConfirmDeleteOrDemoteEmployeeModal] = useState<boolean>(false)
    const [showConfirmRemoveSubstituteModal, setShowConfirmRemoveSubstituteModal] = useState<boolean>(false)

    const [formInstance] = Form.useForm<UserCompanyProfileInterface>()
    const { data: employees = [] } = useEmployees({ excludeDeleted: true })
    const isEmployeeInSubstituteSetting = useMemo(() => {
        if (isNewCompanyProfile) {
            return false
        }
        return (
            employees.filter((employeeItem) =>
                employeeItem.companyProfiles?.some(
                    (companyProfileItem) =>
                        companyProfileItem.companyId.toString() === companyProfile.companyId.toString() &&
                        companyProfileItem.substitutes?.some((substituteItem) => substituteItem.userId === employee.id),
                ),
            ).length > 0
        )
    }, [employees, companyProfile?.companyId, isNewCompanyProfile])
    useEffect(
        () => () => {
            if (isShowing) {
                formInstance.resetFields()
            }
        },
        [isShowing, formInstance],
    )

    const companies = useCompanies()

    /**
     * available companies do not include companies the user is currently part of
     */
    const availableCompanies = useMemo(() => getAvailableCompanies(companies, employee), [companies, employee?.companyProfiles])

    const handleSubmit = async (values: UserCompanyProfileInterface) => {
        try {
            setIsLoading(true)

            if (!isNewCompanyProfile) {
                const profileUpdateResponse = await UserService.submitCompanyProfileUpdate({
                    values,
                    employee,
                    isEmployeeInSubstituteSetting,
                    workflowsReplacementApproverId,
                    removeSubstituteConfirmed,
                    dispatch,
                })
                if (profileUpdateResponse.replacementApproverIdRequired) {
                    setShowConfirmDeleteOrDemoteEmployeeModal(true)
                    return
                }
                if (profileUpdateResponse.substituteRemovalConfirmationRequired) {
                    setShowConfirmRemoveSubstituteModal(true)
                    return
                }

                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:company_profile.updated.title"), t("notification:company_profile.updated.message"))
                if (workflowsReplacementApproverId) {
                    dispatch(fetchAllWorkflows())
                }
            }

            if (isNewCompanyProfile) {
                await UserService.submitNewCompanyProfile({ values, employee, dispatch })
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:company_profile.created.title"), t("notification:company_profile.created.message"))
            }

            handleHide(true)
            await queryClient.invalidateQueries([QueryKeyEnum.USERS])
            await invalidateEmployees()
        } catch (err) {
            if (isNewCompanyProfile) {
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee.create.title"))
            } else {
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee.edit.title"))
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleHide = (isSubmit = false) => {
        setIsLoading(false)
        setWorkflowsReplacementApproverId(undefined)
        formInstance.resetFields()
        if (isSubmit && onSubmit) {
            onSubmit()
        }
        onCancel()
    }

    const handleConfirmRemoveSubstituteCancel = useCallback(() => {
        setShowConfirmRemoveSubstituteModal(false)
    }, [])

    const handleConfirmRemoveSubstituteSubmit = useCallback(() => {
        setShowConfirmRemoveSubstituteModal(false)
        setRemoveSubstituteConfirmed(true)
        formInstance.submit()
    }, [])

    return (
        <>
            <Modal
                title={!isNewCompanyProfile ? `${t("action:company_profile.edit")}: ${companyProfile.company?.name}` : t("action:company_profile.add")}
                open={isShowing}
                maskClosable={false}
                onCancel={() => handleHide()}
                cancelText={t("action:cancel")}
                confirmLoading={isLoading}
                destroyOnClose
                footer={[
                    <Button
                        key="back"
                        onClick={() => {
                            formInstance.resetFields()
                            handleHide()
                        }}
                    >
                        {t("action:cancel")}
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={isLoading}
                        onClick={() => {
                            formInstance.submit()
                        }}
                    >
                        {isNewCompanyProfile ? t("action:add") : t("action:save")}
                    </Button>,
                ]}
            >
                <Form className="p-5" form={formInstance} onFinish={() => handleSubmit(formInstance.getFieldsValue(true))} layout="vertical">
                    <UserCompanyProfileForm
                        isCompanyEditable={isNewCompanyProfile}
                        companyProfile={companyProfile}
                        employee={employee}
                        formInstance={formInstance}
                        onHide={() => handleHide()}
                        availableCompanies={availableCompanies}
                    />
                </Form>
            </Modal>
            {!isNewCompanyProfile && (
                <ConfirmDeleteOrDemoteEmployeeModal
                    isShowing={showConfirmDeleteOrDemoteEmployeeModal}
                    onCancel={() => {
                        setShowConfirmDeleteOrDemoteEmployeeModal(false)
                    }}
                    onSubmit={(_, workflowsReplacementApproverIdVal) => {
                        setShowConfirmDeleteOrDemoteEmployeeModal(false)
                        setWorkflowsReplacementApproverId(workflowsReplacementApproverIdVal)
                        formInstance.submit()
                    }}
                    operationType="demote"
                    employee={employee}
                    companyId={companyProfile?.companyId}
                />
            )}
            {!isNewCompanyProfile && (
                <ConfirmRemoveSubstituteModal
                    isShowing={showConfirmRemoveSubstituteModal}
                    onCancel={handleConfirmRemoveSubstituteCancel}
                    onSubmit={handleConfirmRemoveSubstituteSubmit}
                />
            )}
        </>
    )
}
